import { type ReactNode, useState } from "react";
import { Aviso, Boton, Modal, Tabla } from "../ui/primitivos";

/**
 * El asistente de importación de planillas, compartido (ADR 0016 y 0022).
 *
 * **El archivo no se sube.** Se parsea en el browser y al worker viaja solo el
 * JSON de las filas: eso saca de encima el techo de CPU del Worker y evita
 * guardar un archivo que nadie necesita.
 *
 * El parser se carga con `import()` dinámico **al elegir el archivo**, no al
 * abrir la app: es una librería que solo usa este componente y solo cuando
 * alguien importa, así que no tiene por qué estar en el bundle inicial.
 *
 * **La validación NO se reimplementa acá**: la previsualización llama al mismo
 * endpoint en modo simulación. Lo que el usuario ve es exactamente lo que el
 * servidor va a hacer.
 *
 * Vive suelto y no dentro de la pantalla de clientes porque **son dos
 * importaciones con el mismo flujo**: elegir → mapear → previsualizar →
 * confirmar. Lo que cambia son los campos, el endpoint y —en productos— los
 * controles de vigencia, que entran por `extras`. Dos copias de estos cuatro
 * pasos divergirían en la primera corrección.
 */

export interface CampoPlanilla {
  readonly clave: string;
  readonly nombreAmigable: string;
  readonly obligatorio: boolean;
  readonly descripcion: string;
}

export interface FilaPlanilla {
  readonly [campo: string]: unknown;
}

/** Lo que toda simulación devuelve, sin importar qué se esté importando. */
export interface Simulacion {
  total: number;
  importables: number;
  conError: number;
  filas?: { fila: number; errores: string[] }[];
}

type Paso = "elegir" | "mapear" | "previsualizar" | "listo";

export function AsistenteImportacion<S extends Simulacion>({
  titulo,
  sustantivo,
  campos,
  importar,
  extras,
  resumen,
  onCerrar,
  onImportado,
}: {
  titulo: string;
  /** En singular: "cliente", "precio". Se pluraliza con `(s)`. */
  sustantivo: string;
  campos: readonly CampoPlanilla[];
  /** El mismo endpoint en los dos modos. `simular` no escribe. */
  importar: (filas: FilaPlanilla[], simular: boolean) => Promise<S & { importados?: number }>;
  /** Controles propios de esta importación, en el paso de mapeo. */
  extras?: ReactNode;
  /** Lo que esta importación quiere mostrar de más en la previsualización. */
  resumen?: (simulacion: S) => ReactNode;
  onCerrar: () => void;
  onImportado: () => Promise<void>;
}) {
  const [paso, setPaso] = useState<Paso>("elegir");
  const [columnas, setColumnas] = useState<string[]>([]);
  const [filasCrudas, setFilasCrudas] = useState<Record<string, unknown>[]>([]);
  const [mapeo, setMapeo] = useState<Record<string, string>>({});
  const [simulacion, setSimulacion] = useState<S | null>(null);
  const [importados, setImportados] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [trabajando, setTrabajando] = useState(false);

  async function elegirArchivo(archivo: File) {
    setTrabajando(true);
    setError(null);
    try {
      // Acá y solo acá: el parser entra al bundle recién cuando alguien importa.
      // `/browser` explícito — el paquete no tiene export raíz, y es el build
      // que corre sin `node:*`.
      //
      // `readSheet` y no el export por defecto: el default devuelve TODAS las
      // hojas (`{ sheet, data }[]`), y lo que necesitamos son las filas de la
      // primera. Sin tipos que mientan y sin cast.
      const { readSheet } = await import("read-excel-file/browser");
      const filas = await readSheet(archivo);

      const cabecera = (filas[0] ?? []).map((celda) => String(celda ?? "").trim());
      if (cabecera.length === 0) throw new Error("La planilla no tiene encabezados");

      const datos = filas.slice(1).map((fila) => {
        const objeto: Record<string, unknown> = {};
        cabecera.forEach((columna, i) => {
          objeto[columna] = fila[i] ?? null;
        });
        return objeto;
      });
      if (datos.length === 0) throw new Error("La planilla no tiene filas de datos");

      // Pre-mapeo por nombre: si la planilla usa los nombres sugeridos, no hay
      // nada que elegir. El resto se mapea a mano.
      const inicial: Record<string, string> = {};
      for (const campo of campos) {
        const encontrada = cabecera.find(
          (c) => c.toLowerCase() === campo.nombreAmigable.toLowerCase(),
        );
        if (encontrada) inicial[campo.clave] = encontrada;
      }

      setColumnas(cabecera);
      setFilasCrudas(datos);
      setMapeo(inicial);
      setPaso("mapear");
    } catch (err) {
      setError(
        err instanceof Error ? `No se pudo leer la planilla: ${err.message}` : "Archivo ilegible",
      );
    } finally {
      setTrabajando(false);
    }
  }

  /** Aplica el mapeo columna → campo antes de mandar. */
  function filasMapeadas(): FilaPlanilla[] {
    return filasCrudas.map((fila) => {
      const salida: Record<string, unknown> = {};
      for (const campo of campos) {
        const columna = mapeo[campo.clave];
        if (columna) salida[campo.clave] = fila[columna] ?? null;
      }
      return salida;
    });
  }

  async function previsualizar() {
    setTrabajando(true);
    setError(null);
    try {
      setSimulacion(await importar(filasMapeadas(), true));
      setPaso("previsualizar");
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo validar");
    } finally {
      setTrabajando(false);
    }
  }

  async function confirmar() {
    setTrabajando(true);
    setError(null);
    try {
      const resultado = await importar(filasMapeadas(), false);
      setImportados(resultado.importados ?? resultado.importables ?? 0);
      setPaso("listo");
      await onImportado();
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo importar");
    } finally {
      setTrabajando(false);
    }
  }

  const faltanObligatorios = campos.filter((c) => c.obligatorio && !mapeo[c.clave]);

  return (
    <Modal
      abierto
      onCerrar={onCerrar}
      titulo={titulo}
      descripcion="El archivo se procesa en tu navegador: no se sube a ningún lado"
    >
      <div className="space-y-4">
        {error ? <Aviso>{error}</Aviso> : null}

        {paso === "elegir" ? (
          <div className="space-y-3">
            <p className="text-neutral-600 text-sm">
              Elegí un archivo <code>.xlsx</code>. La primera fila tiene que ser el encabezado.
            </p>
            <input
              type="file"
              accept=".xlsx"
              aria-label="Archivo de planilla"
              onChange={(e) => {
                const archivo = e.target.files?.[0];
                if (archivo) void elegirArchivo(archivo);
              }}
              className="w-full text-neutral-700 text-sm file:mr-3 file:rounded-md file:border-0 file:bg-neutral-200 file:px-3 file:py-1.5 file:text-neutral-800"
            />
            {trabajando ? <p className="text-neutral-500 text-sm">Leyendo la planilla...</p> : null}
          </div>
        ) : null}

        {paso === "mapear" ? (
          <div className="space-y-3">
            <p className="text-neutral-600 text-sm">
              {filasCrudas.length} fila(s). Indicá qué columna corresponde a cada campo.
            </p>
            {extras}
            {campos.map((campo) => (
              <div key={campo.clave} className="flex items-center gap-3">
                <span className="w-48 shrink-0 text-neutral-700 text-sm" title={campo.descripcion}>
                  {campo.nombreAmigable}
                  {campo.obligatorio ? <span className="text-amber-600"> *</span> : null}
                </span>
                <select
                  aria-label={campo.nombreAmigable}
                  value={mapeo[campo.clave] ?? ""}
                  onChange={(e) => setMapeo((prev) => ({ ...prev, [campo.clave]: e.target.value }))}
                  className="flex-1 rounded-md border border-neutral-300 bg-white px-2 py-1 text-neutral-900 text-sm"
                >
                  <option value="">— sin asignar —</option>
                  {columnas.map((columna) => (
                    <option key={columna} value={columna}>
                      {columna}
                    </option>
                  ))}
                </select>
              </div>
            ))}
            {faltanObligatorios.length > 0 ? (
              <Aviso>
                Falta asignar: {faltanObligatorios.map((c) => c.nombreAmigable).join(", ")}
              </Aviso>
            ) : null}
          </div>
        ) : null}

        {paso === "previsualizar" && simulacion ? (
          <div className="space-y-3">
            <div className="flex gap-4 text-sm">
              <span className="text-neutral-600">Total: {simulacion.total}</span>
              <span className="text-emerald-700">Importables: {simulacion.importables}</span>
              <span className={simulacion.conError ? "text-red-700" : "text-neutral-500"}>
                Con error: {simulacion.conError}
              </span>
            </div>

            {resumen?.(simulacion)}

            {simulacion.conError > 0 ? (
              <>
                {/* Todo o nada: una importación a medias deja al usuario sin
                    saber qué entró, y reintentar duplicaría lo que sí entró. */}
                <Aviso>
                  Corregí la planilla y volvé a intentar: si hay errores no se importa nada.
                </Aviso>
                <Tabla cabeceras={["Fila", "Problemas"]}>
                  {(simulacion.filas ?? [])
                    .filter((f) => f.errores.length > 0)
                    .slice(0, 50)
                    .map((f) => (
                      <tr key={f.fila}>
                        <td className="px-4 py-2 text-neutral-600">{f.fila}</td>
                        <td className="px-4 py-2 text-red-700">{f.errores.join(". ")}</td>
                      </tr>
                    ))}
                </Tabla>
              </>
            ) : (
              <Aviso tono="ok">
                Todo listo: {simulacion.importables} {sustantivo}(s) sin problemas.
              </Aviso>
            )}
          </div>
        ) : null}

        {paso === "listo" ? (
          <Aviso tono="ok">
            Se importaron {importados} {sustantivo}(s).
          </Aviso>
        ) : null}
      </div>

      <div className="mt-4 flex justify-end gap-2">
        <Boton variante="secundario" onClick={onCerrar}>
          {paso === "listo" ? "Cerrar" : "Cancelar"}
        </Boton>
        {paso === "mapear" ? (
          <Boton onClick={previsualizar} disabled={trabajando || faltanObligatorios.length > 0}>
            {trabajando ? "Validando..." : "Previsualizar"}
          </Boton>
        ) : null}
        {paso === "previsualizar" ? (
          <Boton onClick={confirmar} disabled={trabajando || (simulacion?.conError ?? 0) > 0}>
            {trabajando ? "Importando..." : `Importar ${simulacion?.importables ?? 0}`}
          </Boton>
        ) : null}
      </div>
    </Modal>
  );
}
